import { useState } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { formatMoney } from '../cashbacks/formatMoney';
import type { Currency } from '../dataProvider/types';
import type { EarningsEntityRow } from './earningsModel';
import { formatRate, formatShortDate } from './format';

/**
 * The per-entity ranked table for one band. Ranked entities (at or above the
 * volume floor) sort by margin rate ascending by default, so the thinnest
 * retained cut (the renegotiate-me signal) sits on top; columns are sortable.
 * Sub-floor entities are listed separately below, UNRANKED, so a low-volume
 * store cannot top the ranking on noise.
 *
 * Reading aids the brief calls for: the commission column is visually DEMOTED
 * (it is roughly flat across brands, an exceptions scan only); rows below the
 * band median margin rate are flagged so outliers jump; a boosted row carries a
 * boost marker and its thin margin reads as intentional, never corrected.
 */

type SortField =
  | 'name'
  | 'volume'
  | 'commission'
  | 'memberSplit'
  | 'margin'
  | 'marginRate';

const COLUMNS: ReadonlyArray<{
  field: SortField;
  label: string;
  numeric: boolean;
}> = [
  { field: 'name', label: 'Entity', numeric: false },
  { field: 'volume', label: 'Volume', numeric: true },
  { field: 'commission', label: 'Commission', numeric: true },
  { field: 'memberSplit', label: 'Member split', numeric: true },
  { field: 'margin', label: 'Margin', numeric: true },
  { field: 'marginRate', label: 'Margin rate', numeric: true },
];

const compare = (
  a: EarningsEntityRow,
  b: EarningsEntityRow,
  field: SortField,
): number => {
  if (field === 'name') return a.name.localeCompare(b.name);
  const av = a[field];
  const bv = b[field];
  // Null margin rate (zero commission) always sorts last.
  if (av == null) return 1;
  if (bv == null) return -1;
  return (av as number) - (bv as number);
};

const MoneyCell = ({
  value,
  currency,
  muted,
  negative,
}: {
  value: number;
  currency: Currency;
  muted?: boolean;
  negative?: boolean;
}) => (
  <TableCell align="right">
    <Typography
      variant="body2"
      component="span"
      color={
        negative ? 'error.main' : muted ? 'text.secondary' : 'text.primary'
      }
      sx={muted ? { opacity: 0.7 } : undefined}
    >
      {formatMoney(value, currency)}
    </Typography>
  </TableCell>
);

const EntityCell = ({
  row,
  belowMedian,
}: {
  row: EarningsEntityRow;
  belowMedian: boolean;
}) => (
  <TableCell>
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      <span>{row.name}</span>
      {row.boost && (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={`boosted, ends ${formatShortDate(row.boost.endsAt)}`}
        />
      )}
      {belowMedian && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="below median"
        />
      )}
    </Box>
  </TableCell>
);

const EntityRow = ({
  row,
  currency,
  belowMedian,
}: {
  row: EarningsEntityRow;
  currency: Currency;
  belowMedian: boolean;
}) => (
  <TableRow>
    <EntityCell row={row} belowMedian={belowMedian} />
    <TableCell align="right">{row.volume}</TableCell>
    <MoneyCell value={row.commission} currency={currency} muted />
    <MoneyCell value={row.memberSplit} currency={currency} />
    <MoneyCell
      value={row.margin}
      currency={currency}
      negative={row.margin < 0}
    />
    <TableCell align="right">{formatRate(row.marginRate)}</TableCell>
  </TableRow>
);

export const EarningsTable = ({
  ranked,
  lowVolume,
  medianMarginRate,
  currency,
}: {
  ranked: EarningsEntityRow[];
  lowVolume: EarningsEntityRow[];
  medianMarginRate: number | null;
  currency: Currency;
}) => {
  const [sortField, setSortField] = useState<SortField>('marginRate');
  const [asc, setAsc] = useState(true);

  const onSort = (field: SortField) => {
    if (field === sortField) {
      setAsc((prev) => !prev);
    } else {
      setSortField(field);
      setAsc(true);
    }
  };

  const sortedRanked = [...ranked].sort((a, b) => {
    const c = compare(a, b, sortField);
    return asc ? c : -c;
  });

  const isBelowMedian = (row: EarningsEntityRow): boolean =>
    medianMarginRate != null &&
    row.marginRate != null &&
    row.marginRate < medianMarginRate;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Band median margin rate: <strong>{formatRate(medianMarginRate)}</strong>
        . Rows below it are flagged.
      </Typography>
      <Table size="small" aria-label="earnings by entity">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.field}
                align={col.numeric ? 'right' : 'left'}
                sx={
                  col.field === 'commission'
                    ? { color: 'text.secondary' }
                    : undefined
                }
              >
                <TableSortLabel
                  active={sortField === col.field}
                  direction={sortField === col.field && !asc ? 'desc' : 'asc'}
                  onClick={() => onSort(col.field)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRanked.map((row) => (
            <EntityRow
              key={row.key}
              row={row}
              currency={currency}
              belowMedian={isBelowMedian(row)}
            />
          ))}

          {lowVolume.length > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ border: 0, pt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Low volume (below the volume floor) - unranked
                  </Typography>
                </TableCell>
              </TableRow>
              {lowVolume.map((row) => (
                <EntityRow
                  key={row.key}
                  row={row}
                  currency={currency}
                  belowMedian={false}
                />
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
